/**
 * Input/textarea com auto-completar de variaveis no padrao `{{varname}}`.
 *
 * Quando o usuario digita `{{`, abre um popover com sugestoes filtradas pela
 * substring posterior. Selecionar uma sugestao insere `{{varname}}` no caret,
 * substituindo a abertura parcial; o cursor fica logo apos `}}` para fluxo
 * continuo de digitacao.
 *
 * Implementacao optei por dropdown absoluto (nao Radix Popover) para evitar
 * complexidade de portals + medicao de caret. O container pai precisa permanecer
 * com `position: relative` (definido aqui via `relative` no wrapper).
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { cn } from '@favelafood/shared/lib/cn';

/**
 * Resultado da deteccao do contexto de auto-completar. Quando `null` o popover
 * deve fechar; caso contrario fornecemos onde o `{{` comeca e a substring digitada
 * apos ele (prefixo de filtragem).
 */
interface VariableContext {
  start: number;
  prefix: string;
}

export interface VariableInputProps {
  /** Texto atual (controlled). */
  value: string;
  /** Callback chamado a cada modificacao do texto. */
  onChange: (next: string) => void;
  /** Lista de variaveis disponiveis para auto-completar. */
  variables: ReadonlyArray<string>;
  /** Quando `true`, renderiza `<textarea>` em vez de `<input>`. */
  multiline?: boolean;
  /** Placeholder padrao do input. */
  placeholder?: string;
  /** Numero de linhas do textarea (so aplica quando `multiline=true`). */
  rows?: number;
  /** Quando `true`, desabilita interacao. */
  disabled?: boolean;
  /** Classes extra aplicadas ao input/textarea. */
  className?: string;
}

/**
 * Detecta se o caret esta dentro de uma abertura `{{` ainda nao fechada.
 *
 * Regras:
 *   - busca o `{{` mais recente antes do caret;
 *   - se nao encontra, retorna `null`;
 *   - se a substring entre `{{` e o caret contem `}}` ou `\n`, considera o
 *     contexto fechado/quebrado e retorna `null`;
 *   - caso contrario, devolve a posicao do `{{` e o que ja foi digitado depois.
 */
function detectVariableContext(text: string, caret: number): VariableContext | null {
  const before = text.slice(0, caret);
  const lastOpen = before.lastIndexOf('{{');
  if (lastOpen < 0) return null;
  const afterOpen = before.slice(lastOpen + 2);
  if (afterOpen.includes('}}') || afterOpen.includes('\n')) return null;
  return { start: lastOpen, prefix: afterOpen };
}

/** Filtra a lista de variaveis pelo prefixo digitado (case-insensitive). */
function filterVariables(
  variables: ReadonlyArray<string>,
  prefix: string,
): ReadonlyArray<string> {
  if (prefix === '') return variables;
  const lower = prefix.toLowerCase();
  return variables.filter((v) => v.toLowerCase().startsWith(lower));
}

const NO_INDEX = -1;

/**
 * Avanca o highlight respeitando os limites da lista (rotativo).
 * Retorna `NO_INDEX` quando a lista esta vazia.
 */
function nextIndex(current: number, length: number, delta: number): number {
  if (length === 0) return NO_INDEX;
  if (current === NO_INDEX) return delta > 0 ? 0 : length - 1;
  const candidate = current + delta;
  if (candidate < 0) return length - 1;
  if (candidate >= length) return 0;
  return candidate;
}

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Reposiciona o caret depois de uma insercao de `{{var}}`. Spec recomenda
 * `setSelectionRange` apos o reflow do React; usamos `requestAnimationFrame`
 * para garantir que o DOM ja refletiu o novo `value` controlado.
 */
function placeCaret(el: FieldElement | null, position: number): void {
  if (el === null) return;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(position, position);
  });
}

/**
 * Constroi o novo texto inserindo `{{name}}` no lugar da abertura parcial e
 * calcula a posicao final do caret.
 */
function applyVariableInsertion(
  source: string,
  caret: number,
  context: VariableContext,
  variable: string,
): { next: string; caret: number } {
  const head = source.slice(0, context.start);
  const tail = source.slice(caret);
  const insertion = `{{${variable}}}`;
  return {
    next: `${head}${insertion}${tail}`,
    caret: context.start + insertion.length,
  };
}

/**
 * `<VariableInput>` — input/textarea com auto-completar de `{{variable}}`.
 *
 * Util em editores de templates (notificacoes, e-mails, mensagens) onde o
 * conteudo aceita placeholders dinamicos resolvidos no backend.
 */
export function VariableInput({
  value,
  onChange,
  variables,
  multiline = false,
  placeholder,
  rows = 4,
  disabled = false,
  className,
}: VariableInputProps): ReactElement {
  const inputRef = useRef<FieldElement | null>(null);
  const [context, setContext] = useState<VariableContext | null>(null);
  const [highlight, setHighlight] = useState(NO_INDEX);
  const listboxId = useId();

  const filtered = useMemo(
    () => (context === null ? [] : filterVariables(variables, context.prefix)),
    [context, variables],
  );
  const open = context !== null && filtered.length > 0;

  // Mantem o highlight valido conforme a lista filtra/encolhe.
  useEffect(() => {
    setHighlight((prev) => {
      if (filtered.length === 0) return NO_INDEX;
      if (prev === NO_INDEX) return 0;
      if (prev >= filtered.length) return 0;
      return prev;
    });
  }, [filtered.length]);

  const updateContext = useCallback((text: string, caret: number) => {
    setContext(detectVariableContext(text, caret));
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<FieldElement>) => {
      const text = e.target.value;
      const caret = e.target.selectionStart ?? text.length;
      onChange(text);
      updateContext(text, caret);
    },
    [onChange, updateContext],
  );

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<FieldElement>) => {
      const target = e.currentTarget;
      const caret = target.selectionStart ?? target.value.length;
      updateContext(target.value, caret);
    },
    [updateContext],
  );

  const insertVariable = useCallback(
    (variable: string) => {
      if (context === null || inputRef.current === null) return;
      const el = inputRef.current;
      const caret = el.selectionStart ?? value.length;
      const result = applyVariableInsertion(value, caret, context, variable);
      onChange(result.next);
      setContext(null);
      setHighlight(NO_INDEX);
      placeCaret(el, result.caret);
    },
    [context, onChange, value],
  );

  const closePopover = useCallback(() => {
    setContext(null);
    setHighlight(NO_INDEX);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<FieldElement>) => {
      if (!open) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlight((prev) => nextIndex(prev, filtered.length, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlight((prev) => nextIndex(prev, filtered.length, -1));
          break;
        case 'Enter':
        case 'Tab': {
          const target = highlight === NO_INDEX ? filtered[0] : filtered[highlight];
          if (target !== undefined) {
            e.preventDefault();
            insertVariable(target);
          }
          break;
        }
        case 'Escape':
          e.preventDefault();
          closePopover();
          break;
        default:
          break;
      }
    },
    [closePopover, filtered, highlight, insertVariable, open],
  );

  const sharedFieldProps = {
    ref: inputRef as React.RefObject<FieldElement>,
    value,
    onChange: handleChange,
    onSelect: handleSelect,
    onKeyDown: handleKeyDown,
    onBlur: () => {
      // Atrasa o close para permitir que cliques nas opcoes (mousedown)
      // disparem `insertVariable` antes do popover desmontar.
      window.setTimeout(closePopover, 120);
    },
    placeholder,
    disabled,
    role: 'combobox' as const,
    'aria-expanded': open,
    'aria-controls': listboxId,
    'aria-autocomplete': 'list' as const,
    'aria-activedescendant':
      open && highlight !== NO_INDEX ? `${listboxId}-opt-${highlight}` : undefined,
    className: cn(
      'w-full rounded-card border border-border bg-bg-input px-3 py-2 text-sm ' +
        'text-white placeholder:text-muted outline-none transition-colors ' +
        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ' +
        'focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60',
      className,
    ),
  };

  return (
    <div className="relative w-full">
      {multiline ? (
        <textarea
          {...sharedFieldProps}
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          rows={rows}
        />
      ) : (
        <input
          {...sharedFieldProps}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
        />
      )}
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className={cn(
            'absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-y-auto ' +
              'rounded-card border border-border bg-bg-card p-1 shadow-2xl animate-fade-in',
          )}
        >
          {filtered.map((variable, index) => {
            const isHighlighted = index === highlight;
            return (
              <li
                key={variable}
                id={`${listboxId}-opt-${index}`}
                role="option"
                aria-selected={isHighlighted}
                className="list-none"
              >
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(e) => {
                    // mousedown evita que o blur do input dispare antes do click.
                    e.preventDefault();
                    insertVariable(variable);
                  }}
                  onMouseEnter={() => setHighlight(index)}
                  className={cn(
                    'flex w-full cursor-pointer select-none items-center gap-2 rounded-md ' +
                      'px-2 py-1.5 text-left text-sm text-white transition-colors',
                    isHighlighted && 'bg-bg-hover',
                  )}
                >
                  <span className="font-mono text-primary">{`{{${variable}}}`}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
